import { apiServerUrl } from "@/Environment";
import type { UpdateTest } from "./connectivityTestTypes";

/**
 * FreightSimple / server-facing connectivity checks: raw domain ping, the
 * typed API client, authenticated core endpoints, infrastructure health, and
 * static asset delivery. See connectivityNetworkChecks for the general
 * network/browser probes.
 */

export async function testDomainResponseTime(
  index: number,
  updateTest: UpdateTest,
): Promise<void> {
  updateTest(index, { status: "running" });

  try {
    const startTime = performance.now();

    const response = await fetch(`${apiServerUrl()}/ping`, {
      method: "GET",
      cache: "no-cache",
    });

    const duration = performance.now() - startTime;

    if (response.ok && duration < 2000) {
      updateTest(index, {
        status: "passed",
        duration: Math.round(duration),
        details: `Resolved in ${Math.round(duration)}ms`,
      });
    } else if (duration < 3000) {
      updateTest(index, {
        status: "warning",
        duration: Math.round(duration),
        details: `Resolved in ${Math.round(duration)}ms (slow)`,
      });
    } else {
      updateTest(index, {
        status: "failed",
        duration: Math.round(duration),
        error: "DNS resolution too slow",
        details: `Took ${Math.round(duration)}ms (exceeded 3s threshold)`,
      });
    }
  } catch (error) {
    updateTest(index, {
      status: "failed",
      error: "DNS resolution failed",
      details: error instanceof Error ? error.message : "Unknown error",
    });
  }
}

export async function testApiConnectivity(
  index: number,
  updateTest: UpdateTest,
  ping: () => Promise<unknown>,
): Promise<void> {
  updateTest(index, { status: "running" });

  try {
    const startTime = performance.now();

    await ping();

    const duration = performance.now() - startTime;

    if (duration < 2000) {
      updateTest(index, {
        status: "passed",
        duration: Math.round(duration),
        details: `Ping successful in ${Math.round(duration)}ms`,
      });
    } else if (duration < 3000) {
      updateTest(index, {
        status: "warning",
        duration: Math.round(duration),
        details: `Ping successful in ${Math.round(duration)}ms (slow)`,
      });
    } else {
      updateTest(index, {
        status: "failed",
        duration: Math.round(duration),
        error: "API connection too slow",
        details: `Ping took ${Math.round(duration)}ms (exceeded 5s threshold)`,
      });
    }
  } catch (error) {
    updateTest(index, {
      status: "failed",
      error: "API connection failed",
      details: error instanceof Error ? error.message : "Unknown error",
    });
  }
}

export async function testCoreEndpoints(
  index: number,
  updateTest: UpdateTest,
  getShipments: () => Promise<unknown>,
  getAllPaymentMethods: () => Promise<unknown>,
): Promise<void> {
  updateTest(index, { status: "running" });

  try {
    const startTime = performance.now();

    const results = await Promise.allSettled([
      getShipments(),
      getAllPaymentMethods(),
    ]);

    const duration = performance.now() - startTime;

    const successfulRequests = results.filter(
      (r) => r.status === "fulfilled",
    ).length;

    const apiNames = ["Shipments API", "Payment Methods API"];
    const failureDetails = results
      .map((result, i) => {
        if (result.status === "fulfilled") {
          return `${apiNames[i]}: OK`;
        }
        const error = result.reason;
        if (error.name === "AbortError") {
          return `${apiNames[i]}: Timeout`;
        } else if (error.message) {
          return `${apiNames[i]}: ${error.message}`;
        }
        return `${apiNames[i]}: Failed`;
      })
      .join(", ");

    if (successfulRequests >= 1 && duration < 3000) {
      updateTest(index, {
        status: "passed",
        duration: Math.round(duration),
        details: `${successfulRequests}/2 APIs responded in ${Math.round(duration)}ms`,
      });
    } else if (duration < 5000) {
      updateTest(index, {
        status: "warning",
        duration: Math.round(duration),
        details: `${successfulRequests}/2 APIs responded in ${Math.round(duration)}ms (${failureDetails})`,
      });
    } else {
      updateTest(index, {
        status: "failed",
        duration: Math.round(duration),
        error: "Core APIs too slow",
        details: `${successfulRequests}/2 APIs responded in ${Math.round(duration)}ms (${failureDetails})`,
      });
    }
  } catch (error) {
    updateTest(index, {
      status: "failed",
      error: "Core APIs test failed",
      details: error instanceof Error ? error.message : "Unknown error",
    });
  }
}

export async function testFreightSimpleInfrastructure(
  index: number,
  updateTest: UpdateTest,
): Promise<void> {
  updateTest(index, { status: "running" });

  try {
    const startTime = performance.now();

    const freightSimpleEndpoints = [
      { name: "Health Check", url: "/api/health" },
      { name: "Status Page", url: "/api/status" },
      { name: "Root Path", url: "/" },
      { name: "API Base", url: "/api" },
    ];

    const results = await Promise.allSettled(
      freightSimpleEndpoints.map((endpoint) =>
        fetch(endpoint.url, {
          method: "HEAD",
          cache: "no-cache",
          signal: AbortSignal.timeout(10000),
        }).then((response) => ({ ...endpoint, response })),
      ),
    );

    const duration = performance.now() - startTime;

    const successfulRequests = results.filter(
      (r) => r.status === "fulfilled" && r.value.response.ok,
    ).length;

    if (successfulRequests >= 2 && duration < 5000) {
      updateTest(index, {
        status: "passed",
        duration: Math.round(duration),
        details: `${successfulRequests}/4 FreightSimple endpoints accessible in ${Math.round(duration)}ms`,
      });
    } else if (successfulRequests >= 1 && duration < 8000) {
      updateTest(index, {
        status: "warning",
        duration: Math.round(duration),
        details: `${successfulRequests}/4 FreightSimple endpoints accessible in ${Math.round(duration)}ms`,
      });
    } else {
      updateTest(index, {
        status: "failed",
        duration: Math.round(duration),
        error: "FreightSimple infrastructure issues",
        details: `${successfulRequests}/4 FreightSimple endpoints accessible in ${Math.round(duration)}ms`,
      });
    }
  } catch (error) {
    updateTest(index, {
      status: "failed",
      error: "FreightSimple infrastructure test failed",
      details: error instanceof Error ? error.message : "Unknown error",
    });
  }
}

export async function testStaticAssets(
  index: number,
  updateTest: UpdateTest,
): Promise<void> {
  updateTest(index, { status: "running" });

  try {
    const startTime = performance.now();

    const testAssets = ["/logo192.png", "/favicon.ico", "/manifest.json"];

    const results = await Promise.allSettled(
      testAssets.map((url) =>
        fetch(url, {
          method: "HEAD",
          cache: "no-cache",
          signal: AbortSignal.timeout(8000),
        }),
      ),
    );

    const duration = performance.now() - startTime;

    const successfulRequests = results.filter(
      (r) => r.status === "fulfilled" && r.value.ok,
    ).length;

    if (successfulRequests >= 2 && duration < 3000) {
      updateTest(index, {
        status: "passed",
        duration: Math.round(duration),
        details: `${successfulRequests}/3 assets loaded in ${Math.round(duration)}ms`,
      });
    } else if (successfulRequests >= 1 && duration < 6000) {
      updateTest(index, {
        status: "warning",
        duration: Math.round(duration),
        details: `${successfulRequests}/3 assets loaded in ${Math.round(duration)}ms`,
      });
    } else {
      updateTest(index, {
        status: "failed",
        duration: Math.round(duration),
        error: "Static asset loading issues",
        details: `${successfulRequests}/3 assets loaded in ${Math.round(duration)}ms`,
      });
    }
  } catch (error) {
    updateTest(index, {
      status: "failed",
      error: "Static asset loading failed",
      details: error instanceof Error ? error.message : "Unknown error",
    });
  }
}
